import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AddTimelineEventModal from '@/components/AddTimelineEventModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TimelineView({ user }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, membersRes] = await Promise.all([
        axios.get(`${API}/timeline`, { withCredentials: true }),
        axios.get(`${API}/members`, { withCredentials: true })
      ]);
      setEvents(eventsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (eventData) => {
    try {
      await axios.post(`${API}/timeline`, eventData, {
        withCredentials: true
      });
      toast.success('Event added successfully');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.member_id === memberId);
    return member ? member.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
          className="mb-6 text-foreground-muted hover:text-primary"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">Family Timeline</h1>
            <p className="text-base font-sans text-foreground-muted">
              Important milestones and events
            </p>
          </div>

          <Button
            onClick={() => setShowAddModal(true)}
            data-testid="add-event-btn"
            className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif tracking-wide"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Add Event
          </Button>
        </div>

        {events.length === 0 ? (
          <div className="bg-white border border-border shadow-card rounded-lg p-12 text-center">
            <CalendarIcon className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-serif text-foreground mb-2">No Events Yet</h3>
            <p className="text-base font-sans text-foreground-muted mb-6">
              Start documenting your family's journey
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              data-testid="empty-add-event-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif tracking-wide"
            >
              Add First Event
            </Button>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

            {/* Events */}
            <div className="space-y-8">
              {events.map((event, index) => (
                <div key={event.event_id} className="relative pl-20" data-testid={`timeline-event-${index}`}>
                  {/* Timeline dot */}
                  <div className="absolute left-6 w-5 h-5 rounded-full bg-primary border-4 border-background"></div>

                  {/* Event card */}
                  <div className="bg-white border border-border shadow-card rounded-lg p-6 hover:shadow-md transition-all">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-xl font-serif text-foreground" data-testid="event-title">
                          {event.title}
                        </h3>
                        {event.member_id && (
                          <p className="text-sm font-sans text-foreground-muted">
                            {getMemberName(event.member_id)}
                          </p>
                        )}
                      </div>
                      <span className="text-sm font-mono text-muted-foreground uppercase tracking-widest" data-testid="event-date">
                        {event.date}
                      </span>
                    </div>
                    {event.description && (
                      <p className="text-base font-sans text-foreground-muted leading-relaxed" data-testid="event-description">
                        {event.description}
                      </p>
                    )}
                    <span className="inline-block mt-3 px-3 py-1 text-xs font-sans bg-accent text-accent-foreground rounded-full">
                      {event.event_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddTimelineEventModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddEvent}
          members={members}
        />
      )}
    </div>
  );
}